// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';
import { m, LazyMotion, domAnimation } from 'framer-motion';

// Components
import Accordion from '../accordion/accordion';
import Content from '../content/content';
import Banner from '../banner/banner';
import PressArticleSlider from '../pressArticleSlider/pressArticleSlider';
import CallToActionBanner from '../callToActionBanner/callToActionBanner';
import TwoColumnLarge from '../twoColumnLarge/twoColumnLarge';
import Boxes from '../boxes/boxes';
import LocationsMap from '../locationsMap/locationsMap';
import StackedBoxes from '../stackedBoxes/stackedBoxes';
import Steps from '../steps/steps';
import TwoColumn from '../twoColumn/twoColumn';
import LatestBlogPosts from '../../blog/blogLatestPosts/blogLatestPosts';
import QaModals from '../qaModals/qaModals';
import Timeline from '../timeline/timeline';
import EnquireForm from '../enquireForm/enquireForm';
import ReferYourFriendForm from '../referYourFriendForm/referYourFriendForm';
import Offices from '../offices/offices';
import PressArticles from '../pressArticles/pressArticles';
import Team from '../team/team';
import Notice from '../notice/notice';
import Endorsements from '../endorsements/endorsements';
import SuccessStories from '../successStories/successStories';
import SmartmatchSubmissionForm from '../smartmatchSubmissionForm/smartmatchSubmissionForm';
import MeetTheMatchmakers from '../meetTheMatchmakers/meetTheMatchmakers';
import Typeform from '../typeform/typeform';
import RelatedPosts from '../relatedPosts/relatedPosts';

const layoutMap = {
  Accordion,
  Banner,
  Boxes,
  CallToActionBanner,
  Content,
  EnquireForm,
  Endorsements,
  LatestBlogPosts,
  LocationsMap,
  Notice,
  Offices,
  PressArticles,
  PressArticleSlider,
  ReferYourFriendForm,
  SmartmatchSubmissionForm,
  StackedBoxes,
  Steps,
  SuccessStories,
  Team,
  Timeline,
  TwoColumn,
  TwoColumnLarge,
  MeetTheMatchmakers,
  Typeform,
  RelatedPosts,

  // Predefined content
  MatchmakingSteps: Steps,
  QAModals: QaModals,
  Services: StackedBoxes,
  SeoPageServices: StackedBoxes,
  MeetTheFounder: TwoColumnLarge,
};

export default function FlexibleContentFactory({
  layouts,
  pageDisplayOptions,
}) {
  const firstLayoutIsContent =
    layouts && layouts.length
      ? layouts[0]?.fieldGroupName ===
        'ContentNode_Flexiblecontent_FlexibleContent_Content'
      : false;

  const layoutRenderer = ({ layout, index }) => {
    if (!layout || !layout?.fieldGroupName) return null;

    const options = {
      name: layout?.fieldGroupName,
      key: uuid(),
      spacing: layout?.spacing || 'standard',
      width: layout?.width || 'none',
      height: layout?.height || 'none',
      displaysTitle: pageDisplayOptions?.displayTitle,
      isFirstLayout: index === 0 && !pageDisplayOptions?.displayTitle,
      imageLoading:
        index === 0 || (index === 1 && firstLayoutIsContent) ? 'eager' : 'lazy',
      predefined: false,
    };

    // Convert layout name from GraphQL to component name
    options.name = options.name.replace(
      'ContentNode_Flexiblecontent_FlexibleContent_',
      '',
    );

    // Retrieve layout from the component map
    const Component = layoutMap[options.name];

    // Determine if any unique props are required for the layout
    switch (options.name) {
      case 'Services':
        options.predefined = 'services';
        break;
      case 'SeoPageServices':
        options.predefined = 'seoPageServices';
        break;
      case 'MatchmakingSteps':
        options.predefined = 'matchmakingSteps';
        break;
      case 'MeetTheFounder':
        options.predefined = 'meetTheFounder';
        break;
      default:
        options.predefined = null;
    }

    // List of any layouts which should not be animated
    const doNotAnimate = [
      'ContentNode_Flexiblecontent_FlexibleContent_Notice',
      'ContentNode_Flexiblecontent_FlexibleContent_Team',
    ];

    return (
      <m.div
        key={options.key}
        className={`flexible-content-layout spacing-${options.spacing} width-${options.width} height-${options.height}`}
        initial={
          index !== 0 && !doNotAnimate.includes(layout?.fieldGroupName)
            ? 'hidden'
            : 'visible'
        }
        whileInView="visible"
        viewport={{ once: true }}
        transition={{
          type: 'spring',
          mass: 0.35,
          stiffness: 75,
          duration: 0.4,
          delay: 0.3,
        }}
        variants={{
          visible: { opacity: 1, y: 0 },
          hidden: { opacity: 0, y: 100 },
        }}
      >
        <Component
          layout={layout}
          isFirstLayout={options.isFirstLayout}
          predefined={options.predefined}
          imageLoading={options.imageLoading}
        />
      </m.div>
    );
  };

  return (
    <div className="flexible-content">
      <LazyMotion features={domAnimation}>
        {layouts?.length &&
          layouts.map((layout, index) => layoutRenderer({ layout, index }))}
      </LazyMotion>
    </div>
  );
}

FlexibleContentFactory.propTypes = {
  layouts: PropTypes.arrayOf(
    PropTypes.shape({
      fieldGroupName: PropTypes.string,
    }),
  ),
  pageDisplayOptions: PropTypes.shape({
    displayTitle: PropTypes.bool,
  }),
};

FlexibleContentFactory.defaultProps = {
  layouts: [],
  pageDisplayOptions: {},
};
